import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Notifications, useToast } from "@/components/ui/toast";
import { keys } from "@/hooks/keys";

// The webhook that flips the plan can land a few seconds after Stripe redirects
// back, so a single refetch on return often still reads the free tier.
const PLAN_REFRESH_DELAYS_MS = [0, 3_000, 8_000, 15_000];

/**
 * Says what happened when Stripe Checkout sends the customer back. The success
 * URL is a plain redirect anyone can open, so it promises a pending switch, not
 * a payment.
 *
 * Checkout returns to whatever page it was opened from with `?checkout=success`
 * or `?checkout=cancelled`. Nothing read that, so a cancel looked like nothing
 * happened and a payment left the header on "Free plan" until the next poll.
 * Mounted in the top bar so it covers every page checkout can start from.
 */
export function CheckoutReturnNotice(): React.ReactElement | null {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast, error, showToast, hideToast, dismissError } = useToast();
  const param = searchParams.get("checkout");
  // Held in state because clearing the parameter changes `param` straight
  // away, and an effect keyed on it would cancel its own refresh timers.
  const [outcome, setOutcome] = React.useState<"success" | "cancelled" | null>(null);

  React.useEffect(() => {
    if (param !== "success" && param !== "cancelled") return;
    setOutcome(param);
    // Drop the parameter so a reload or a shared link does not repeat it.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("checkout");
      return next;
    }, { replace: true });
  }, [param, setSearchParams]);

  React.useEffect(() => {
    if (!outcome) return;
    if (outcome === "cancelled") {
      showToast("Checkout canceled. You have not been charged.", "info");
      return;
    }
    showToast("Thanks. We are confirming your payment, and your plan switches to Scale as soon as it clears.", "success");
    const timers = PLAN_REFRESH_DELAYS_MS.map((ms) =>
      window.setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: keys.aiUsage.any() });
        void queryClient.invalidateQueries({ queryKey: ["billing"] });
      }, ms),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [outcome, showToast, queryClient]);

  return <Notifications toast={toast} error={error} onCloseToast={hideToast} onDismissError={dismissError} />;
}
